import { Check, Gift } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { Button } from "@/v2/components/ui/button";
import { Chip } from "@/v2/components/ui/chip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/v2/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { TIER_META, tierLabel } from "../../tierMeta";
import type { BillingCycle } from "../../types";

export function StepChooseGift({
  channelName,
  billing,
  setBilling,
  termCount,
  setTermCount,
  safeTermMax,
  availableTiers,
  selectedPlan,
  setSelectedPlan,
  pricesReady,
  pricesError,
  loadOptions,
  getMonthlyDisplayPrice,
  getAnnualDisplayPrice,
}: {
  channelName: string;
  billing: BillingCycle;
  setBilling: (b: BillingCycle) => void;
  termCount: number;
  setTermCount: (n: number) => void;
  safeTermMax: number;
  availableTiers: string[];
  selectedPlan: string;
  setSelectedPlan: (tier: string) => void;
  pricesReady: boolean;
  pricesError: boolean;
  loadOptions: () => void;
  getMonthlyDisplayPrice: (tier: string) => string;
  getAnnualDisplayPrice: (tier: string) => string;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
          Choose Your Gift
        </h1>
        <p className="text-sm mt-1 max-w-xl text-muted-foreground">
          Select the plan you'd like to gift. They'll get unlimited access to
          every game, live and on-demand, across all {channelName} leagues.
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-[1.25rem] sm:p-[2rem] space-y-6 shadow-xs">
        {/* BILLING CYCLE */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-3 block text-muted-foreground">
            BILLING CYCLE
          </p>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-lg border border-border overflow-hidden bg-card">
              <button
                type="button"
                onClick={() => setBilling("monthly")}
                style={{
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                className={cn(
                  "font-bold text-sm transition-colors",
                  billing === "monthly"
                    ? "bg-action text-white"
                    : "bg-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBilling("annual")}
                style={{
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                className={cn(
                  "font-bold text-sm transition-colors",
                  billing === "annual"
                    ? "bg-action text-white"
                    : "bg-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                Annual
              </button>
            </div>
            <Chip color="var(--v2-success)" size="sm">
              BEST VALUE
            </Chip>
          </div>
        </div>

        {/* SUBSCRIPTION LENGTH */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-3 block text-muted-foreground">
            SUBSCRIPTION LENGTH
          </p>
          <div className="flex items-center gap-6">
            <div className="w-15">
              <Select
                value={String(termCount)}
                onValueChange={(val) => setTermCount(Number(val))}
              >
                <SelectTrigger className="bg-card border-border">
                  <SelectValue placeholder="Length" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {Array.from({ length: safeTermMax }, (_, i) => i + 1).map(
                    (n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <span className="text-base font-medium text-muted-foreground">
              {billing === "annual"
                ? termCount > 1
                  ? "years"
                  : "year"
                : termCount > 1
                  ? "months"
                  : "month"}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-xs font-semibold underline hover:opacity-80 text-action"
                  >
                    How long is this?
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    Choose how many{" "}
                    {billing === "annual" ? "years" : "months"} of access to
                    gift. The recipient gets the full period.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* CHOOSE A PLAN */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-3 block text-muted-foreground">
            CHOOSE A PLAN
          </p>

          {!pricesReady && !pricesError ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-48 rounded-xl border border-border bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : pricesError ? (
            <div className="rounded-xl border border-destructive bg-destructive/10 p-4 text-center">
              <p className="text-sm font-semibold text-destructive mb-2">
                Couldn't load current pricing.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadOptions}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {availableTiers.map((tier) => {
                const isSel = selectedPlan === tier;
                const meta = TIER_META[tier] || {
                  label: tierLabel(tier),
                  description: "",
                  features: ["Full channel access"],
                };
                const isInsider = tier === "insider";
                const mDisp = getMonthlyDisplayPrice(tier);
                const aDisp = getAnnualDisplayPrice(tier);

                return (
                  <button
                    type="button"
                    key={tier}
                    onClick={() => setSelectedPlan(tier)}
                    className={cn(
                      "relative rounded-xl p-4 border-2 transition-all cursor-pointer flex flex-col text-left w-full min-w-[170px] bg-card",
                      isSel ? "border-action" : "border-border",
                    )}
                  >
                    {/* Pinned to a fixed corner (not part of the title row)
                        so it lands at the same spot on every card regardless
                        of whether that card's BEST VALUE badge pushes the
                        title down. Plain span (not the shadcn Radio
                        primitive) to match the reliable radio dot pattern
                        already used on the Plans page
                        (AccessTypeToggle/BillingCycleToggle). */}
                    <span
                      className={cn(
                        "absolute top-4 right-4 size-5 rounded-full border-2 transition-colors",
                        isSel ? "border-action bg-action" : "border-slate-300",
                      )}
                    />

                    {isInsider && (
                      <div className="mb-3 self-start">
                        <Chip color="var(--v2-success)" size="sm">
                          BEST VALUE
                        </Chip>
                      </div>
                    )}

                    <div className="pr-7 mb-2">
                      <h3
                        className="font-black text-xl"
                        style={{
                          color: isSel
                            ? "var(--v2-action)"
                            : "var(--v2-foreground)",
                        }}
                      >
                        {meta.label}
                      </h3>
                    </div>

                    <p className="text-sm whitespace-pre-line mb-4 min-h-[36px] text-muted-foreground">
                      {meta.description}
                    </p>

                    <div className="space-y-2 mb-6">
                      {meta.features.map((feat, fIdx) => (
                        <div
                          key={fIdx}
                          className="flex items-center gap-2 text-sm font-semibold text-foreground"
                        >
                          <Check className="size-3.5 shrink-0 stroke-[2.5] text-action" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-3 border-t border-border space-y-0.5 mt-auto">
                      <div className="flex items-baseline gap-1">
                        <span
                          className={cn(
                            "text-2xl font-extrabold",
                            isSel ? "text-action" : "text-foreground",
                          )}
                        >
                          ${mDisp}
                        </span>
                        <span className="text-xs font-medium text-muted-foreground">
                          / month
                        </span>
                      </div>
                      {billing === "annual" && (
                        <p className="text-[11px] font-medium text-muted-foreground">
                          <span>${aDisp}</span> / year
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* BOTTOM INFO BANNER */}
        <div className="rounded-xl bg-muted p-3 flex items-center gap-3">
          <div className="size-9 rounded-full bg-action/10 flex items-center justify-center shrink-0">
            <Gift className="size-4 text-action" />
          </div>
          <div className="space-y-0.5">
            <p className="text-base font-semibold leading-snug text-foreground">
              The gift includes full access for the entire billing period.
            </p>
            <p className="text-sm leading-snug text-muted-foreground">
              The recipient can redeem it for a new or existing {channelName}{" "}
              account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
